/**
 * AdminPersons — Super-admin picker to open a Person Profile.
 *
 * Route: /admin/persons
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UserCircle, Search, ArrowRight, Users } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminPersons() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');

  const listQ = useQuery({
    queryKey: ['persons', applied],
    queryFn: async () => {
      const url = `${API}/api/admin/identity-registry/persons?q=${encodeURIComponent(applied)}&limit=50`;
      const r = await axios.get(url, { headers: authH() });
      return r.data;
    },
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-persons-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold">
            <Users className="h-7 w-7 text-violet-400" />
            Persons Directory
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Central identity registry. Open any Person to see their memberships,
            workflow history, generated documents and finance transactions in one view.
          </p>
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="persons-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setApplied(q); }}
                placeholder="Name, email, phone or username"
                className="border-slate-700 bg-slate-950 pl-9"
              />
            </div>
            <Button
              data-testid="persons-search-btn"
              onClick={() => setApplied(q)}
              className="bg-violet-600 hover:bg-violet-500"
            >
              Search
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {listQ.isLoading ? 'Loading…' : `${total} person(s)`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {items.map((p) => (
                <button
                  key={p.id}
                  data-testid="person-card"
                  onClick={() => navigate(`/admin/persons/${p.id}`)}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-left hover:border-violet-500/50 hover:bg-slate-900"
                >
                  <div className="flex items-center gap-3">
                    <UserCircle className="h-10 w-10 text-violet-400" />
                    <div>
                      <div className="font-medium text-slate-100">
                        {p.first_name || p.last_name
                          ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
                          : (p.name || p.username)}
                      </div>
                      <div className="text-xs text-slate-500">{p.username}</div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-500" />
                </button>
              ))}
              {!listQ.isLoading && items.length === 0 && (
                <div className="col-span-2 py-10 text-center text-slate-500" data-testid="persons-empty">
                  No persons match your search.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
